import { h, type VNode } from 'vue';
import type { DataTableColumns } from 'naive-ui';
import {
  ReviewTaskStatus,
  type FileReviewTableSearch,
  type GaiaJsonValue,
  type ReviewExtractionInDb,
} from '@/api';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaGridCell from '@/components/design/domain/grid/GaiaGridCell.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import { reviewCellStateMap } from '@/components/design/statusMaps';
import { formatDisplayValue } from '@/utils/displayAiResponse';
import { safeStringify } from '@/utils/jsonHelpers';

/**
 * The insight grid's columns and rows: one row per file, one column per
 * extraction prompt.
 *
 * Lifted out of `DocumentInsightTableView` — ~700 lines of `h()` in a view that
 * also owns credit gating, prompt CRUD and three dialogs. The column builder
 * needs callbacks rather than the view's refs, which is what makes it testable
 * and what stops the view from re-deriving cell state a second way.
 */

/** Which part of an extraction the grid is showing. */
export type InsightDisplayMode = 'value' | 'reason' | 'page' | 'highlighted_text' | 'evaluation';

/** One extraction, flattened into what a cell needs. */
export interface ExtractionCellData {
  uid: string;
  value: GaiaJsonValue;
  reason: string | null;
  page: number | null;
  processing_state: ReviewTaskStatus;
  highlighted_text: string[] | null;
  prompt_name?: string;
  file_review_uid?: string;
  field_type?: string | object;
  evaluation?: { answer?: string; reason?: string } | null;
  approved?: boolean;
  human_overwrite?: {
    uid?: string;
    value?: GaiaJsonValue;
    reason?: string | null;
    page?: number | null;
    highlighted_text?: string[] | null;
    evaluation?: GaiaJsonValue;
    evaluation_reason?: string | null;
  } | null;
}

export interface TableRowData {
  filename: string;
  file_info: {
    file_name: string;
    vault_file_uid?: string | null;
  };
  file_review_uid: string;
  [key: string]: ExtractionCellData | string | Record<string, unknown>;
}

/** The three keys of a row that are not extraction columns. */
const ROW_META_KEYS = new Set(['filename', 'file_info', 'file_review_uid']);

/** Filter value standing for "this prompt never ran against this file". */
export const NO_DATA_FILTER = '__no_data__';

/** Read `{answer, reason}` off an evaluation that may be a bare string. */
function readEvaluation(evaluation: GaiaJsonValue): { answer?: string; reason?: string } | null {
  if (evaluation === null || evaluation === undefined) return null;
  if (typeof evaluation === 'string') return { answer: evaluation };
  const record = evaluation as Record<string, unknown>;
  return {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to undefined
    answer: (record.answer as string | undefined) || undefined,
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to undefined
    reason: (record.reason as string | undefined) || undefined,
  };
}

function toCell(extraction: ReviewExtractionInDb, fileReviewUid: string): ExtractionCellData {
  const overwrite = extraction.human_overwrite;
  return {
    uid: extraction.uid,
    value: extraction.value ?? null,
    reason: extraction.reason,
    page: extraction.page,
    processing_state: extraction.processing_state,
    highlighted_text: extraction.highlighted_text,
    prompt_name: extraction.static_prompt_copy.name,
    file_review_uid: fileReviewUid,
    field_type: extraction.static_prompt_copy.response_type,
    evaluation: readEvaluation(extraction.evaluation),
    approved: extraction.approved,
    human_overwrite: overwrite
      ? {
          uid: overwrite.uid,
          value: overwrite.value,
          reason: overwrite.reason,
          page: overwrite.page,
          highlighted_text: overwrite.highlighted_text,
          evaluation: overwrite.evaluation,
          evaluation_reason: overwrite.evaluation_reason,
        }
      : null,
  };
}

/**
 * Flatten the search response into grid rows, keyed by prompt name.
 *
 * Takes `FileReviewTableSearch[]` rather than `unknown[]`: the previous version
 * cast every field off `Record<string, unknown>` — about sixty casts — for data
 * the generated client already types.
 */
export function transformToTableRowData(rows: FileReviewTableSearch[]): TableRowData[] {
  return rows.map(row => {
    const rowData: TableRowData = {
      filename: row.static_vault_file_copy.file_name || 'Unknown File',
      file_info: {
        file_name: row.static_vault_file_copy.file_name || 'Unknown File',
        vault_file_uid: row.static_vault_file_copy.uid,
      },
      file_review_uid: row.uid,
    };

    for (const extraction of row.columns ?? []) {
      const promptName = extraction.static_prompt_copy.name || 'Unknown Prompt';
      rowData[promptName] = toCell(extraction, row.uid);
    }

    return rowData;
  });
}

/** The prompt-name columns present across a set of rows, sorted. */
export function columnKeysOf(rows: TableRowData[]): string[] {
  const keys = new Set<string>();
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (!ROW_META_KEYS.has(key)) keys.add(key);
    }
  }
  return [...keys].sort();
}

function cellOf(row: TableRowData, key: string): ExtractionCellData | undefined {
  const value = row[key];
  return value && typeof value === 'object' && 'uid' in value
    ? (value as ExtractionCellData)
    : undefined;
}

/** The stored value behind a cell for the active display mode, overwrite first. */
export function getActualValue(
  cell: ExtractionCellData | undefined,
  mode: InsightDisplayMode
): unknown {
  if (!cell) return null;
  const overwrite = cell.human_overwrite;

  switch (mode) {
    case 'page':
      return overwrite ? (overwrite.page ?? null) : cell.page;
    case 'reason':
      return overwrite ? (overwrite.reason ?? null) : cell.reason;
    case 'highlighted_text':
      return overwrite ? (overwrite.highlighted_text ?? null) : cell.highlighted_text;
    case 'evaluation':
      return overwrite ? (overwrite.evaluation ?? null) : (cell.evaluation?.answer ?? null);
    default:
      return overwrite ? (overwrite.value ?? null) : cell.value;
  }
}

/**
 * What a cell reads as. `null`/`undefined` show as `N/A`; an empty string or
 * empty list shows as `Empty` — the two say different things about a run.
 */
export function getDisplayValue(
  cell: ExtractionCellData | undefined,
  mode: InsightDisplayMode
): string | number | null {
  if (!cell) return null;

  const modeMap = {
    page: { key: 'page', format: 'raw' as const },
    reason: { key: 'reason', format: 'str' },
    highlighted_text: { key: 'highlighted_text', format: 'list[str]' },
    evaluation: { key: 'evaluation', format: 'str' },
    value: { key: 'value', format: cell.field_type },
  } as const;

  const { key, format } = modeMap[mode];

  const overwriteValue = cell.human_overwrite
    ? (cell.human_overwrite as Record<string, unknown>)[key]
    : undefined;

  let rawValue: unknown;
  if (overwriteValue !== undefined) {
    rawValue = overwriteValue;
  } else if (key === 'evaluation') {
    rawValue = cell.evaluation?.answer ?? null;
  } else {
    rawValue = (cell as unknown as Record<string, unknown>)[key] ?? null;
  }

  return format === 'raw'
    ? (rawValue as string | number | null)
    : formatDisplayValue(rawValue, format);
}

/** Per-column filter options, derived from every row the grid knows about. */
export function buildFilterOptions(
  rows: TableRowData[],
  mode: InsightDisplayMode
): Record<string, { label: string; value: string }[] | undefined> {
  const optionsMap: Record<string, { label: string; value: string }[] | undefined> = {};
  if (rows.length === 0) return optionsMap;

  for (const key of columnKeysOf(rows)) {
    const values = new Set<string>();
    let hasCrossOutCells = false;

    for (const row of rows) {
      const cell = cellOf(row, key);
      if (!cell) {
        hasCrossOutCells = true;
        continue;
      }
      const displayValue = getDisplayValue(cell, mode);
      if (displayValue !== null) values.add(String(displayValue));
    }

    const options = [...values].map(value => ({ label: value, value }));
    if (hasCrossOutCells) options.push({ label: '✕ (No data)', value: NO_DATA_FILTER });
    optionsMap[key] = options;
  }

  return optionsMap;
}

/**
 * Display string → the value behind it, per column. The filter menu offers
 * display strings, but a backend filter has to send the stored value.
 */
export function buildDisplayToActualValueMaps(
  rows: TableRowData[],
  mode: InsightDisplayMode
): Record<string, Map<string, unknown> | undefined> {
  const maps: Record<string, Map<string, unknown> | undefined> = {};

  for (const key of columnKeysOf(rows)) {
    const map = new Map<string, unknown>();
    for (const row of rows) {
      const cell = cellOf(row, key);
      if (!cell) continue;
      const displayValue = getDisplayValue(cell, mode);
      if (displayValue !== null) {
        map.set(String(displayValue), getActualValue(cell, mode));
      }
    }
    maps[key] = map;
  }

  return maps;
}

/**
 * Cache key over everything that changes what the columns render. Rebuilding
 * ~40 columns of `h()` on every poll tick is what this avoids.
 */
export function generateColumnsCacheKey(
  rows: TableRowData[],
  mode: InsightDisplayMode,
  filterRowCount: number
): string {
  if (rows.length === 0) return 'empty';

  const keys = columnKeysOf(rows);
  const dataHash = rows
    .map(row =>
      keys
        .map(key => {
          const cell = cellOf(row, key);
          if (!cell) return 'null';
          const value = getDisplayValue(cell, mode);
          return `${key}:${value}:${cell.human_overwrite ? 'overwrite' : 'original'}`;
        })
        .join('|')
    )
    .join('||');

  return `${keys.join('|')}:${dataHash}:filterRows:${filterRowCount}`;
}

export interface BuildInsightColumnsOptions {
  /** The page of rows on screen — what the columns are built for. */
  rows: TableRowData[];
  /** Every row the grid knows about, for filter options. */
  filterRows: TableRowData[];
  mode: InsightDisplayMode;
  /** The org's configured colour for one evaluation answer, if it has one. */
  answerColor: (columnKey: string, answer: string) => string | undefined;
  onOpenCell: (row: TableRowData, columnKey: string) => void;
  onOpenContract: (row: TableRowData) => void;
}

export function buildInsightColumns(
  options: BuildInsightColumnsOptions
): DataTableColumns<TableRowData> {
  const { rows, filterRows, mode, answerColor, onOpenCell, onOpenContract } = options;
  if (rows.length === 0) return [];

  const filterOptions = buildFilterOptions(filterRows.length > 0 ? filterRows : rows, mode);

  const columns: DataTableColumns<TableRowData> = [
    {
      title: 'Filename',
      key: 'filename',
      fixed: 'left',
      width: 300,
      sorter: true,
      filterOptions: [...new Set(rows.map(row => row.filename))].map(value => ({
        label: value,
        value,
      })),
      filter: (value, row) => row.filename === value,
      render: (row): VNode =>
        h(
          'div',
          {
            style: {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
              width: '100%',
            },
          },
          [
            h(GaiaText, { size: 'dense', truncate: true }, () => row.filename),
            h(
              GaiaButton,
              {
                size: 'tiny',
                onClick: (event: Event) => {
                  event.stopPropagation();
                  onOpenContract(row);
                },
              },
              () => 'Open'
            ),
          ]
        ),
    },
  ];

  for (const key of columnKeysOf(rows)) {
    const columnFilterOptions = filterOptions[key] ?? [];

    columns.push({
      // Just the name. naive spends the rest of this cell on its own sort
      // arrow and filter funnel, which collide with anything else put here —
      // column management lives in the toolbar's Columns popover instead.
      title: key,
      key,
      width: 200,
      sorter: true,
      filterOptions: columnFilterOptions.length > 0 ? columnFilterOptions : undefined,
      filter: (value, row) => {
        const cell = cellOf(row, key);
        // A prompt that never ran against this file — the grid crosses it out,
        // and this is how you filter down to those.
        if (value === NO_DATA_FILTER) return !cell;
        if (!cell) return false;
        const displayValue = getDisplayValue(cell, mode);
        return displayValue !== null && String(displayValue) === value;
      },
      cellProps: row => {
        const cell = cellOf(row, key);
        // Annotated `unknown`: an overwrite's evaluation is untyped JSON, and
        // without this TS widens the `??` chain to `{}` and calls the null
        // check dead.
        const raw: unknown = cell?.human_overwrite?.evaluation ?? cell?.evaluation?.answer ?? null;
        if (raw === null) return {};
        const answer = typeof raw === 'string' ? raw : safeStringify(raw);
        const color = answerColor(key, answer);
        return color ? { style: { backgroundColor: color } } : {};
      },
      render: (row): VNode => {
        const cell = cellOf(row, key);

        // No extraction at all: this file ran under a different prompt
        // collection. Distinct from "N/A", which means it ran and found nothing.
        if (!cell) {
          return h(GaiaGridCell, {
            frameless: true,
            state: 'blank',
            title: 'This prompt did not run against this file',
          });
        }

        const text = String(getDisplayValue(cell, mode) ?? '');
        const descriptor = reviewCellStateMap[cell.processing_state];
        const isDone = cell.processing_state === ReviewTaskStatus.DONE;

        return h(GaiaGridCell, {
          frameless: true,
          state: descriptor.state,
          text: isDone ? text : descriptor.label,
          title: isDone ? text : descriptor.label,
          approved: Boolean(cell.approved),
          overwritten: Boolean(cell.human_overwrite),
          overwriteTitle: 'Manually overwritten',
          onSelect: () => {
            onOpenCell(row, key);
          },
        });
      },
    });
  }

  return columns;
}
